import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css, Style } from "@tento-ui/component-styles";
import type { ChatPluginManager } from "./chat-block-plugin";
import type { ChatTurn } from "./chat-turn";
import type { ChatMessage } from "./chat-types";

// The debug register: a turn is its own data, printed.
//
// Every other register renders what a message MEANS — bubbles, prose, rows.
// This one renders what a message IS: the ChatMessage object, stringified,
// exactly as the thread holds it. It exists for the developer reading a
// surface: flip the theme and the same conversation becomes its wire form,
// streaming included — the thread mutates the message in place as deltas
// land, so re-stringifying on every delta shows the object growing live.
//
// The plugin manager is deliberately unused: plugins render block MEANING,
// and here the block's raw shape is the content. The mechanics — height
// reporting, eager-then-observed measurement, recycle discipline — are the
// same as every other turn, which is what keeps this a register instead of
// a special case.
@component({ name: "std-chat-debug-turn" })
export class ChatDebugTurn extends LonaWebComponent implements ChatTurn {
  private $content: HTMLElement = this.$("content");
  private $json: HTMLElement = this.$("json");

  private message: Option<ChatMessage> = null;
  private currentIndex: Option<number> = null;
  private resizeObserver: Option<ResizeObserver> = null;
  private lastHeight: number = 0;

  onHeightMeasured: Option<(index: number, height: number) => void> = null;
  onMessageClick: Option<(messageId: string) => void> = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      // offsetHeight, never contentRect: the turn gap is PADDING on
      // #content, which contentRect excludes — the report would drop it.
      const height = this.$content.offsetHeight;
      if (height > 0 && height !== this.lastHeight && this.currentIndex != null) {
        this.lastHeight = height;
        this.style.height = height + "px";
        this.onHeightMeasured?.(this.currentIndex, height);
      }
    });
    this.resizeObserver.observe(this.$content);

    if (this.currentIndex != null && this.lastHeight === 0) {
      this.measureNow();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  bind(message: ChatMessage, _manager: ChatPluginManager, index: number): void {
    this.message = message;
    this.currentIndex = index;
    this.toggleAttribute("user", message.role === "user");
    this.toggleAttribute("assistant", message.role !== "user");
    this.render();
    this.measureNow();
  }

  private render(): void {
    if (!this.message) return;
    this.$json.textContent = JSON.stringify(this.message, null, 2);
  }

  private measureNow(): void {
    const height = this.$content.offsetHeight;
    if (height > 0) {
      this.lastHeight = height;
      this.style.height = height + "px";
      if (this.currentIndex != null) {
        this.onHeightMeasured?.(this.currentIndex, height);
      }
    }
  }

  // The thread already mutated the message's block before calling either of
  // these, so the stored reference is current — re-stringify and remeasure.
  appendDelta(_blockIndex: number, _delta: string): void {
    this.render();
    this.measureNow();
  }

  finalizeBlock(_blockIndex: number): void {
    this.render();
    this.measureNow();
  }

  recycle(): void {
    this.$json.textContent = "";
    this.message = null;
    this.lastHeight = 0;
    this.currentIndex = null;
    this.onHeightMeasured = null;
    this.onMessageClick = null;
    this.toggleAttribute("user", false);
    this.toggleAttribute("assistant", false);
  }

  onRecycle(): void {
    super.onRecycle();
    this.recycle();
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        display: block;
        width: 100%;
        box-sizing: border-box;
        overflow: hidden;
      }

      :host([debug-boundaries]) {
        outline: 1px solid var(--red, red);
        outline-offset: -1px;
      }

      /* Absolute for the same reason every turn's wrapper is: the host's
         height is the virtualizer's coordinate, while the content renders
         at its natural height and reports it. */
      #content {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        padding-block-end: var(--chat-debug-turn-gap, 12px);
        /* The row's own gutter — the thread never pads horizontally. */
        padding-inline: var(--chat-turn-inset, 0px);
      }

      /* The register's own face: a debug printout is monospace by nature,
         the way a bubble's chrome is the bubble's own. The ground stays the
         thread's — themes own grounds. */
      #json {
        margin: 0;
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: var(--chat-debug-turn-size, 11px);
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-word;
        border-left: 2px solid var(--chat-debug-turn-edge, color-mix(in srgb, currentColor 25%, transparent));
        padding-inline-start: 10px;
        opacity: 0.9;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="content"><pre id="json"></pre></div>
  `;
}
